import { graphql, fetchQuery, type Environment } from 'react-relay';
import { OPERAND_TYPE_KEYWORD, OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import type {
	SearchTemplate,
	LozengeOption,
	Option,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { FormatMessage, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { OperandValues } from '../../../../../../common/constants.tsx';
import type { PropsResolver, PropTypes } from '../../../../../../common/types.tsx';
import {
	fireJqlBuilderBasicErrorAnalytics,
	generateErrorOptions,
	generateOptions,
} from '../../../../../../common/utils.tsx';
import { getEmptyLabel } from '../../../../../../utils/get-empty-label/index.tsx';
import { getFunctionLabel } from '../../../../../../utils/get-function-label/index.tsx';
import { getValidOperandNames } from '../../../../../../utils/get-valid-operands.tsx';
import type {
	getCommonPropsQuery,
	getCommonPropsQuery$data as commonPropsResolver$data,
	JiraJqlViewContext,
} from './__generated__/getCommonPropsQuery.graphql';
import { getLozengeAppearance } from './utils.tsx';

type Params = {
	fieldId: string;
	propType: PropTypes;
	showOperandDisplayValuesInFullList?: boolean;
	formatMessage: FormatMessage;
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	operandDisplayValues?: string[];
	searchTemplate: SearchTemplate;
	viewContext: JiraJqlViewContext | null;
};

type CommonNode = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<
				NonNullable<NonNullable<commonPropsResolver$data['jira']>['jqlBuilder']>['fieldValues']
			>['edges']
		>[number]
	>['node']
>;

export const getOption = (node: CommonNode, itemType: 'value'): Option | LozengeOption => {
	const { displayName, jqlTerm, __typename } = node;

	const option: Option = {
		label: displayName,
		value: jqlTerm,
		optionType: 'option',
		itemType,
	};

	if (__typename === 'JiraJqlPriorityFieldValue' && node.priority && node.priority.iconUrl) {
		return {
			...option,
			icon: node.priority.iconUrl,
		};
	}

	if (
		(__typename === 'JiraJqlStatusFieldValue' ||
			__typename === 'JiraJqlStatusCategoryFieldValue') &&
		node.statusCategory
	) {
		const { colorName } = node.statusCategory;
		return {
			...option,
			optionType: 'lozenge',
			appearance: getLozengeAppearance(colorName),
			isBold: fg('jira_jql_builder_bold_status') ? true : undefined,
		};
	}

	if (
		__typename === 'JiraJqlIssueFieldValue' &&
		node.issue &&
		node.issue.fields &&
		node.issue.fields.edges &&
		node.issue.fields.edges[0] &&
		node.issue.fields.edges[0].node &&
		node.issue.fields.edges[0].node.text !== undefined &&
		node.issue.fields.edges[0].node.text !== null
	) {
		return {
			...option,
			label: node.issue.fields.edges[0].node.text,
			secondaryLabel: option.label,
		};
	}

	return option;
};
export const getCommonProps = ({
	fieldId,
	propType,
	showOperandDisplayValuesInFullList,
	formatMessage,
	jql,
	environment,
	cloudId,
	operandDisplayValues,
	searchTemplate,
	viewContext,
}: Params): PropsResolver => ({
	enableFullList: false,
	operandDisplayValues,
	showOperandDisplayValuesInFullList: showOperandDisplayValuesInFullList || undefined,
	loadOptions: async (search, renderInitialValues, endCursor) => {
		const after = endCursor;

		try {
			const data = await fetchQuery<getCommonPropsQuery>(
				environment,
				graphql`
					query getCommonPropsQuery(
						$cloudId: ID!
						$first: Int = 10
						$jqlTerm: String!
						$jql: String!
						$searchString: String!
						$after: String
						$viewContext: JiraJqlViewContext
					) {
						jira @required(action: THROW) {
							jqlBuilder(cloudId: $cloudId) {
								fieldValues(
									first: $first
									jqlTerm: $jqlTerm
									jqlContext: $jql
									searchString: $searchString
									after: $after
									viewContext: $viewContext
								) {
									totalCount
									pageInfo {
										endCursor
									}
									edges {
										node {
											jqlTerm
											displayName
											__typename
											... on JiraJqlStatusFieldValue {
												statusCategory {
													colorName
												}
											}
											... on JiraJqlPriorityFieldValue {
												priority {
													iconUrl
												}
											}
											... on JiraJqlStatusCategoryFieldValue {
												statusCategory {
													colorName
												}
											}
											... on JiraJqlIssueFieldValue {
												issue {
													fields {
														edges {
															node {
																... on JiraSingleLineTextField {
																	text
																}
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				`,
				{
					cloudId,
					first: 10,
					jqlTerm: fieldId.toLowerCase(),
					jql: jql || '',
					searchString: search,
					after,
					viewContext,
				},
				{ fetchPolicy: 'store-or-network' },
			).toPromise();
			const operandOptions: Option[] = showOperandDisplayValuesInFullList
				? (getValidOperandNames(propType) ?? []).map((name) => ({
						label:
							OperandValues[name].type === OPERAND_TYPE_KEYWORD
								? getEmptyLabel(searchTemplate ?? null, formatMessage)
								: getFunctionLabel(name, formatMessage),
						value: OperandValues[name].value,
						optionType: 'option',
						itemType: OperandValues[name].type,
					}))
				: [];
			if (!data?.jira?.jqlBuilder?.fieldValues?.edges) {
				return generateOptions([...operandOptions]);
			}

			const { edges: fieldValues, totalCount, pageInfo } = data.jira.jqlBuilder.fieldValues;
			return generateOptions(
				[
					...(search.length === 0 && !endCursor ? operandOptions : []),
					...fieldValues.reduce<Array<Option | LozengeOption>>((options, edges) => {
						if (!edges || !edges.node) {
							return options;
						}

						const { node } = edges;
						options.push(getOption(node, OPERAND_TYPE_VALUE));

						return options;
					}, []),
				],
				totalCount,
				pageInfo?.endCursor || undefined,
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireJqlBuilderBasicErrorAnalytics('commonJqlBuilderBasicFieldValues', searchTemplate, error);
			return generateErrorOptions(error);
		}
	},
});
